import { POWERUP_LIST, PowerupId, POWERUPS } from "@/shared/data/powerups";
import { useGameStore } from "@/shared/state/gameStore";
import { AvatarAnimation } from "@/shared/types/animation";
import { DerivedStats, GameState } from "@/shared/types/game";
import {
  applyPowerupPurchaseToState,
  canPurchasePowerup,
  computeDerivedStats,
  getPowerupCost,
  reconcileOwnedPowerupState,
} from "@/shared/utils/powerups";
import { EventBus, RuntimeEventMap } from "./systems/event";
import { StreamChatMessage } from "@/shared/types/chat";
import { ANIMATIONS } from "@/shared/data/animations";
import {
  AnimationEffect,
  UnlockableAnimation,
} from "@/shared/types/unlock";
import { addTimedBuff, updateGame } from "@/shared/utils/game";
import { createGeneratedChatMessage } from "@/shared/utils/chat";

export interface ResolvedSimulationEvents {
  state: GameState;
  spawnedChats: number;
  spawnedBadComments: number;
  spawnedGifts: number;
  giftLikesAwarded: number;
}

const BAD_COMMENT_VIEWER_PENALTY = 0.8;

export class GameRuntime {
  private bus = new EventBus<RuntimeEventMap>();

  private cachedDerivedStats: DerivedStats | null = null;
  private lastEmittedLikes: number | null = null;

  private derivedStatsDirty = true;

  public on = this.bus.on.bind(this.bus);

  getState(): GameState {
    return useGameStore.getState();
  }

  setState(nextState: GameState) {
    useGameStore.setState(reconcileOwnedPowerupState(nextState, POWERUP_LIST));
    this.markDerivedStatsDirty();
  }

  tick(dt: number) {
    let state = this.getState();
    if (state.run.status !== "live") {
      return;
    }

    state = updateGame(state, dt);
    state = this.runAutomation(state);

    const resolved = this.resolveSimulationEvents(state);
    state = resolved.state;

    this.setState(state);

    for (let i = 0; i < resolved.spawnedChats; i++) {
      this.bus.emit("chatSpawned", {});
      this.bus.emit("chatMessageSpawned", {
        message: createGeneratedChatMessage(
          state,
          Math.random() < 0.35 ? "morale" : "nice",
        ),
      });
    }

    for (let i = 0; i < resolved.spawnedBadComments; i++) {
      this.bus.emit("badCommentSpawned", {});
      this.bus.emit("chatMessageSpawned", {
        message: createGeneratedChatMessage(
          state,
          "bad",
          BAD_COMMENT_VIEWER_PENALTY,
        ),
      });
      this.bus.emit("avatarReaction", {
        reaction: "embarrassed",
      });
    }

    for (let i = 0; i < resolved.spawnedGifts; i++) {
      this.bus.emit("giftSpawned", { amount: 25 });
    }

    this.emitHudEvents();
  }

  clickLike() {
    const state = this.getState();
    if (state.run.status !== "live") {
      return;
    }

    const stats = this.getDerivedStats(state);
    const gainedLikes = stats.likesPerClick;

    const nextState: GameState = {
      ...state,
      likes: state.likes + gainedLikes,
    };

    this.setState(nextState);
    this.bus.emit("likeClicked", {
      amount: gainedLikes,
    });
    this.emitHudEvents();
  }

  moderateChatMessage(message: StreamChatMessage) {
    const state = this.getState();
    if (state.run.status !== "live") {
      return;
    }

    let nextRealViewers = state.viewers.real;
    let reaction: GameState["avatarReaction"] = null;

    if (message.kind === "bad") {
      nextRealViewers += Math.max(0, message.penaltyViewers ?? 0);
      reaction = "celebrate";
    }

    if (message.kind === "nice" || message.kind === "morale") {
      nextRealViewers = Math.max(0, nextRealViewers - 1);
      reaction = "embarrassed";
    }

    if (nextRealViewers !== state.viewers.real) {
      this.setState({
        ...state,
        viewers: {
          ...state.viewers,
          real: nextRealViewers,
        },
      });
      this.emitHudEvents();
    }

    if (reaction) {
      this.bus.emit("avatarReaction", {
        reaction,
      });
    }

    this.bus.emit("chatMessageModerated", {
      message,
    });
  }

  expireChatMessage(message: StreamChatMessage) {
    const state = this.getState();
    if (state.run.status !== "live") {
      return;
    }

    if (message.kind !== "bad") {
      return;
    }

    const penalty = Math.max(0, message.penaltyViewers ?? 0);

    if (penalty <= 0) {
      return;
    }

    this.setState({
      ...state,
      viewers: {
        ...state.viewers,
        real: Math.max(0, state.viewers.real - penalty),
      },
    });
    this.bus.emit("avatarReaction", {
      reaction: "embarrassed",
    });
    this.bus.emit("chatMessageExpired", {
      message,
    });
    this.emitHudEvents();
  }

  buyPowerup(powerupId: PowerupId): boolean {
    const state = this.getState();
    if (state.run.status !== "live") return false;

    const def = POWERUPS[powerupId];
    const level = state.powerups[powerupId] ?? 0;

    if (!def) return false;
    if (!canPurchasePowerup(state, def)) return false;

    const cost = getPowerupCost(def, level);

    const purchasedState: GameState = {
      ...state,
      likes: state.likes - cost,
      powerups: {
        ...state.powerups,
        [powerupId]: level + 1,
      },
    };
    const nextState =
      powerupId === "retirement"
        ? {
            ...purchasedState,
            run: {
              status: "won" as const,
            },
          }
        : applyPowerupPurchaseToState(purchasedState, def);

    this.markAllDerivedDirty();
    this.setState(nextState);

    this.bus.emit("powerupPurchased", {
      powerupId,
      level: level + 1,
    });

    if (powerupId === "retirement") {
      this.bus.emit("runWon", {});
    }

    this.emitHudEvents();
    return true;
  }

  activateAnimation(animationId: UnlockableAnimation): boolean {
    const state = this.getState();
    if (state.run.status !== "live") {
      return false;
    }

    const nextState = this.tryActivateAnimationOnState(state, animationId);
    const definition = ANIMATIONS[animationId];

    if (nextState === state || !definition) {
      return false;
    }

    this.setState(nextState);
    this.bus.emit("animationPlayed", {
      animationId: definition.avatarAnimation,
    });

    this.emitHudEvents();
    return true;
  }

  private tryActivateAnimationOnState(
    state: GameState,
    animationId: UnlockableAnimation,
  ): GameState {
    const stats = this.getDerivedStats(state);

    const unlocked = stats.unlockedAnimations[animationId] ?? false;
    if (!unlocked) return state;
    if (state.activeAnimation) return state;

    const cooldownRemaining = state.cooldowns.animations[animationId] ?? 0;
    if (cooldownRemaining > 0) return state;

    const def = ANIMATIONS[animationId];
    if (!def) return state;

    const powerMultiplier =
      stats.animationPowerMultiplierById[animationId as AvatarAnimation] ?? 1;

    let nextState = state;

    for (const effect of def.effects) {
      nextState = this.applyAnimationEffect(
        nextState,
        animationId,
        effect,
        powerMultiplier,
      );
    }

    const cooldownMultiplier =
      stats.animationCooldownMultiplierById[animationId as AvatarAnimation] ??
      1;

    nextState = {
      ...nextState,
      activeAnimation: {
        id: animationId,
        remaining: def.durationMs / 1000,
        duration: def.durationMs / 1000,
      },
      cooldowns: {
        ...nextState.cooldowns,
        animations: {
          ...nextState.cooldowns.animations,
          [animationId]: def.baseCooldown * cooldownMultiplier,
        },
      },
    };

    return nextState;
  }

  private applyAnimationEffect(
    state: GameState,
    animationId: UnlockableAnimation,
    effect: AnimationEffect,
    powerMultiplier: number,
  ): GameState {
    switch (effect.type) {
      case "add_real_viewers": {
        return {
          ...state,
          viewers: {
            ...state.viewers,
            real: state.viewers.real + effect.amount * powerMultiplier,
          },
        };
      }

      case "add_botted_viewers": {
        return {
          ...state,
          viewers: {
            ...state.viewers,
            botted: state.viewers.botted + effect.amount * powerMultiplier,
          },
        };
      }

      case "likes_per_second_multiplier": {
        return addTimedBuff(state, {
          id: `${animationId}_lps_${crypto.randomUUID()}`,
          type: "likes_per_second_multiplier",
          multiplier: effect.multiplier * powerMultiplier,
          remaining: effect.duration,
        });
      }

      case "likes_per_click_multiplier": {
        return addTimedBuff(state, {
          id: `${animationId}_lpc_${crypto.randomUUID()}`,
          type: "likes_per_click_multiplier",
          multiplier: effect.multiplier * powerMultiplier,
          remaining: effect.duration,
        });
      }

      case "real_viewer_decay_multiplier": {
        return addTimedBuff(state, {
          id: `${animationId}_decay_${crypto.randomUUID()}`,
          type: "real_viewer_decay_multiplier",
          multiplier: effect.multiplier,
          remaining: effect.duration,
        });
      }

      case "gift_rate_multiplier": {
        return addTimedBuff(state, {
          id: `${animationId}_gift_${crypto.randomUUID()}`,
          type: "gift_rate_multiplier",
          multiplier: effect.multiplier,
          remaining: effect.duration,
        });
      }

      case "chat_rate_multiplier": {
        return addTimedBuff(state, {
          id: `${animationId}_chat_${crypto.randomUUID()}`,
          type: "chat_rate_multiplier",
          multiplier: effect.multiplier,
          remaining: effect.duration,
        });
      }

      case "bad_comment_rate_multiplier": {
        return addTimedBuff(state, {
          id: `${animationId}_bad_${crypto.randomUUID()}`,
          type: "bad_comment_rate_multiplier",
          multiplier: effect.multiplier,
          remaining: effect.duration,
        });
      }

      case "animation_cooldown_multiplier": {
        return addTimedBuff(state, {
          id: `${animationId}_cdr_${crypto.randomUUID()}`,
          type: "animation_cooldown_multiplier",
          target: effect.target,
          multiplier: effect.multiplier,
          remaining: effect.duration,
        });
      }

      default: {
        return state;
      }
    }
  }

  private getDerivedStats(state: GameState): DerivedStats {
    if (!this.derivedStatsDirty && this.cachedDerivedStats) {
      return this.cachedDerivedStats;
    }

    const stats = computeDerivedStats(state, POWERUP_LIST);
    this.cachedDerivedStats = stats;
    this.derivedStatsDirty = false;
    return stats;
  }

  private markDerivedStatsDirty() {
    this.derivedStatsDirty = true;
    this.cachedDerivedStats = null;
  }

  private markAllDerivedDirty() {
    this.markDerivedStatsDirty();
  }

  private runAutomation(state: GameState): GameState {
    if (state.run.status !== "live") {
      return state;
    }

    let nextState = state;
    const stats = this.getDerivedStats(nextState);

    for (const animationId of Object.keys(
      stats.automationById,
    ) as AvatarAnimation[]) {
      if (!(stats.automationById[animationId] ?? false)) continue;

      const cooldown = nextState.cooldowns.animations[animationId] ?? 0;
      const unlocked = stats.unlockedAnimations[animationId] ?? false;

      if (!unlocked || cooldown > 0) continue;

      const updatedState = this.tryActivateAnimationOnState(
        nextState,
        animationId,
      );

      if (updatedState !== nextState) {
        nextState = updatedState;
        const definition = ANIMATIONS[animationId];

        if (definition) {
          this.bus.emit("animationPlayed", {
            animationId: definition.avatarAnimation,
          });
        }
      }
    }

    return nextState;
  }

  private resolveSimulationEvents(state: GameState): ResolvedSimulationEvents {
    let nextState = state;

    let spawnedChats = 0;
    let spawnedBadComments = 0;
    let spawnedGifts = 0;
    let giftLikesAwarded = 0;

    while (nextState.simulation.chatProgress >= 1) {
      nextState = {
        ...nextState,
        simulation: {
          ...nextState.simulation,
          chatProgress: nextState.simulation.chatProgress - 1,
        },
      };
      spawnedChats += 1;
    }

    while (nextState.simulation.badCommentProgress >= 1) {
      nextState = {
        ...nextState,
        simulation: {
          ...nextState.simulation,
          badCommentProgress: nextState.simulation.badCommentProgress - 1,
        },
      };
      spawnedBadComments += 1;
    }

    while (nextState.simulation.giftProgress >= 1) {
      nextState = {
        ...nextState,
        likes: nextState.likes + 25,
        simulation: {
          ...nextState.simulation,
          giftProgress: nextState.simulation.giftProgress - 1,
        },
      };
      spawnedGifts += 1;
      giftLikesAwarded += 25;
    }

    return {
      state: nextState,
      spawnedChats,
      spawnedBadComments,
      spawnedGifts,
      giftLikesAwarded,
    };
  }

  private emitHudEvents() {
    const state = this.getState();
    const nextLikes = state.likes;
    const nextViewerTotal = state.viewers.real + state.viewers.botted;
    const previousLikes = this.lastEmittedLikes ?? 0;
    const likesDelta = nextLikes - previousLikes;

    this.bus.emit("likesChanged", {
      total: nextLikes,
      delta: likesDelta,
    });

    this.bus.emit("viewersChanged", {
      real: state.viewers.real,
      botted: state.viewers.botted,
      total: nextViewerTotal,
    });

    this.lastEmittedLikes = nextLikes;
  }

  resetRun() {
    const store = useGameStore.getState();
    store.resetGame();
    this.cachedDerivedStats = null;
    this.derivedStatsDirty = true;
    this.lastEmittedLikes = null;
    this.emitHudEvents();
  }
}
